/* eslint-disable complexity */
import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { usePageVisibility } from './usePageVisibility';
import { setHotel, showErrors, showNonPrimeHotelErrors } from '../../redux/currentSearch/actions';
import {
    getNonPrimeHotelRoom,
    getResidentDiscountProps,
    getValidationErrorsSelector,
    isFlightValidSearch,
    isHotelProductActive,
    isHotelValidSearch,
    isInspirationalSearch,
} from '../../redux/currentSearch/selectors';
import { isHotelStandaloneValidSearch } from '../../redux/currentSearch/hotelStandalone.selectors';
import {
    ABUtils,
    BuyPathContextProp,
    Config,
    DateUtils,
    DynpackRoomUtils,
    LocalStorageUtils,
    SessionStorageUtils,
    TrackingUtils,
    withBuyPathContext,
    withPageContext,
    WMPUtils,
} from '../../utils';
import {
    BuyPath,
    EntryPoint,
    EntryPointProp,
    ErrorType,
    LocationHotelsSuppliers,
    ProductManagerErrorProp,
    SearchProp,
    TabType,
    TripType,
} from '../../models';
import { SearchManagerContext } from '../SearchManager/Context';
import { SearchService } from '../../services';
import { CAMPAIGNS } from '../HotelManager/Campaigns';
import { useCarsSearchExtension } from '../CarsSearchExtension';
import { SEARCH_EXTENSION_HOTEL_HOME } from '../../utils/HtmlEvents';
import { getAsyncSearch } from '../../redux/asyncSearch/actions';

const isCampaignActive = () => {
    return CAMPAIGNS.includes(WMPUtils.getCampaignId()) && WMPUtils.isCampaignNotExpired();
};

const getHsaURL = ({ currentSearch, hotelStandalone }) => {
    const defaultBuyPath =
        Config.get('entryPoint') === 'hotels' ? BuyPath.HSA_HOME : BuyPath.HSA_HOME_TAB;

    return SearchService.getSearchURL({
        ...currentSearch,
        hotelStandalone,
        buyPath: isCampaignActive()
            ? BuyPath.HSA_LANDING_WMP
            : Config.get('isWMP')
                ? BuyPath.HSA_WMP_HOME_TAB
                : defaultBuyPath,
    });
};

const SEARCH_EXTENSION_NON_PRIME_STORAGE_KEY = 'search-extension-flight-search';

export const SearchButton = ({
    activeProduct,
    astId,
    buyPaths,
    currentSearch,
    dynPack,
    hotelStandalone,
    hotelPrime,
    isInspirationalSearch,
    isFlightValidSearch,
    isHotelValidSearch,
    isHotelStandaloneValidSearch,
    render,
    setHotel,
    shouldShowResident,
    showErrors,
    trackEvent,
    validationErrors,
    extraTracking,
    showNonPrimeHotelErrors,
    isHotel,
    nonPrimeRoom,
    onErrorCallback,
    getAsyncSearch,
}) => {
    const [loading, setLoading] = useState(false);
    const [tryToLaunchSearch, setTryToLaunchSearch] = useState(false);
    const searchManagerContext = useContext(SearchManagerContext);
    const bookingExtensionSearchURL = Config.get('bookingExtensionSearchURL');
    const isVisible = usePageVisibility();
    const isPrime = Config.get('isPrimeCookieActive');
    const isLogged = Config.get('isUserLoggedIn');
    const { tabsStatus: carsSearchExtensionTabsStatus } = useCarsSearchExtension();

    const isFlightsTab = activeProduct === EntryPoint.FLIGHTS;
    const { isHSAHotelFunnel } = ABUtils.getABs();

    const dispatch = useDispatch();
    const { isFetching, searchValue } = useSelector((state) => state.asyncSearch);
    const searchID = searchValue?.id;

    const openSearchUrl = useCallback(
        ({ searchID }) => {
            const defaultBuyPath =
                Config.get('entryPoint') === 'hotels' ? BuyPath.HSA_HOME : BuyPath.HSA_HOME_TAB;
            window.location.href = SearchService.getSearchURL({
                ...currentSearch,
                searchId: searchID,
                astId,
                hotelStandalone,
                buyPath: isCampaignActive()
                    ? BuyPath.HSA_LANDING_WMP
                    : Config.get('isWMP')
                        ? BuyPath.HSA_WMP_HOME_TAB
                        : defaultBuyPath,
            });
        },
        [currentSearch, hotelStandalone],
    );

    useEffect(() => {
        if (!isFetching && searchID) {
            openSearchUrl({ searchID });
        }
    }, [isFetching, searchID, openSearchUrl]);

    const flightSearchExtensionEnabled =
        isFlightsTab && !dynPack && isHSAHotelFunnel && isPrime && isLogged;

    const flightSearchExtensionToHomePagePublicEnabled =
        isFlightsTab && !dynPack && isHSAHotelFunnel && !isLogged && !isPrime;

    const flightSearchExtensionToHomePageFencedEnabled =
        isFlightsTab && !dynPack && isHSAHotelFunnel && isLogged && !isPrime;

    const openCarsAndHotelWindows = useCallback(() => {
        const hotelSearchURL = getHsaURL({ currentSearch, hotelStandalone });
        const newWindow = window.open(hotelSearchURL, '_blank');
        if (newWindow) {
            const carsSearchUrl = SearchService.getCarsSearchExtensionURL({
                activeProduct,
                currentSearch,
            });
            if (carsSearchUrl) {
                window.history.pushState({}, '');
                window.location.replace(carsSearchUrl);
            }
        } else {
            dispatch(getAsyncSearch(currentSearch));
        }
    }, [activeProduct, currentSearch, hotelStandalone, dispatch, getAsyncSearch]);

    const doHotelSearch = useCallback(() => {
        if (carsSearchExtensionTabsStatus[activeProduct].isEnabled) {
            openCarsAndHotelWindows();
        } else {
            dispatch(getAsyncSearch(currentSearch));
        }
    }, [
        dispatch,
        getAsyncSearch,
        currentSearch,
        carsSearchExtensionTabsStatus,
        activeProduct,
        openCarsAndHotelWindows,
    ]);

    const doDynpackSearch = useCallback(() => {
        const dpSearchURL = SearchService.getSearchURL({
            ...currentSearch,
            shouldShowResident,
            dynPack,
            activeProduct,
            buyPath: buyPaths.DP,
        });
        if (carsSearchExtensionTabsStatus[activeProduct].isEnabled) {
            const newWindow = window.open(dpSearchURL, '_blank');

            if (newWindow) {
                const carsSearchUrl = SearchService.getCarsSearchExtensionURL({
                    activeProduct,
                    dynPack,
                    currentSearch,
                });

                if (carsSearchUrl) {
                    window.history.pushState({}, '');
                    window.location.replace(carsSearchUrl);
                }
            } else {
                window.location.href = dpSearchURL;
            }
        } else {
            window.location.href = dpSearchURL;
        }
    }, [
        activeProduct,
        buyPaths,
        currentSearch,
        dynPack,
        shouldShowResident,
        carsSearchExtensionTabsStatus,
    ]);

    const doSearch = useCallback(() => {
        const trainSearch = activeProduct === EntryPoint.TRAINS ? { trainSearch: true } : {};
        const flightSearchData = {
            ...currentSearch,
            shouldShowResident,
            activeProduct,
            buyPath: isInspirationalSearch
                ? BuyPath.INSPIRE_ME
                : activeProduct === EntryPoint.TRAINS
                    ? buyPaths.TRAINS
                    : buyPaths.FLIGHTS,
            bookingExtensionSearchURL,
            ...trainSearch,
        };

        if (
            carsSearchExtensionTabsStatus[activeProduct].isEnabled &&
            (currentSearch.tripType === TripType.ROUND_TRIP ||
                currentSearch.tripType === TripType.ONE_WAY) &&
            !isInspirationalSearch
        ) {
            const flightSearchURL = SearchService.getSearchURL(flightSearchData);

            const newWindow = window.open(flightSearchURL, '_blank');

            if (newWindow) {
                const carsSearchUrl = SearchService.getCarsSearchExtensionURL({
                    activeProduct,
                    dynPack,
                    currentSearch,
                });

                if (carsSearchUrl) {
                    window.history.pushState({}, '');
                    window.location.replace(carsSearchUrl);
                }
            } else {
                searchManagerContext.onSearch(flightSearchData);
            }
        } else {
            searchManagerContext.onSearch(flightSearchData);
        }
    }, [
        activeProduct,
        bookingExtensionSearchURL,
        buyPaths,
        carsSearchExtensionTabsStatus,
        currentSearch,
        isInspirationalSearch,
        searchManagerContext,
        shouldShowResident,
        dynPack,
    ]);

    const showSearchErrors = useCallback(() => {
        showNonPrimeHotelErrors();
        showErrors();
        onErrorCallback();
    }, [onErrorCallback, showErrors, showNonPrimeHotelErrors]);

    const trackErrorEvents = useCallback(() => {
        const product = dynPack ? TabType.DP : isHotel ? TabType.HOTELS : TabType.FLIGHTS;
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        const { tripType } = currentSearch;
        validationErrors
            .filter((error) => error.type !== ErrorType.SEPARATE_STAY)
            .forEach((error) => {
                if (!isHotel) {
                    const label = TrackingUtils.LABEL.error(error, tripType);
                    trackEvent({ action, label });
                } else if (error.type !== ErrorType.DEPARTURE) {
                    const label = TrackingUtils.LABEL.hotelSearchError({ error });
                    trackEvent({ action, label });
                }
            });
        const label = TrackingUtils.LABEL.errorEvent({ activeProduct: product });
        trackEvent({ action, label });
    }, [activeProduct, currentSearch, dynPack, isHotel, trackEvent, validationErrors]);

    const trackSearchEvents = useCallback(() => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];

        trackEvent({ action, label: TrackingUtils.LABEL.product({ isHotel, dynPack }) });

        if (dynPack) {
            const rooms = DynpackRoomUtils.getRoomSelection(
                currentSearch.pax,
                currentSearch.rooms,
                activeProduct,
            );
            const roomsLabel = TrackingUtils.LABEL.roomSelection(rooms);
            SessionStorageUtils.setValue('LOAD_RESULTS_DP_ACCOMMODATION', Date.now());
            trackEvent({ action, label: roomsLabel });
        } else if (isHotel) {
            const rooms = currentSearch.rooms.length;
            let adults = 0;
            let children = 0;

            currentSearch.rooms.forEach((room) => {
                adults += room.adults;
                children += room.kids.length;
            });

            const hotelSearchLabel = TrackingUtils.LABEL.hotelSearchLabel({
                rooms,
                adults,
                children,
            });
            TrackingUtils.trackClickEvent({ id: `home-astid-${astId}` });
            trackEvent({ action, label: hotelSearchLabel });
            trackEvent({
                action,
                label: TrackingUtils.LABEL.hotelsDestinationTypeSearch({
                    ...currentSearch,
                    rooms,
                    adults,
                    children,
                    supplier: isHotel && hotelStandalone ? LocationHotelsSuppliers.HSA : '',
                }),
            });
        } else {
            const numPax = TrackingUtils.LABEL.numberPax(currentSearch);
            trackEvent({ action, label: numPax });
            trackEvent({ action, label: TrackingUtils.LABEL.cabinClass(currentSearch) });
            trackEvent({ action, label: TrackingUtils.LABEL.tripType(currentSearch) });
        }

        extraTracking();

        if (shouldShowResident) {
            const residentLabel = TrackingUtils.LABEL.resident(currentSearch);
            trackEvent({ action, label: residentLabel });
        }
    }, [
        activeProduct,
        currentSearch,
        dynPack,
        extraTracking,
        hotelStandalone,
        isHotel,
        shouldShowResident,
        trackEvent,
    ]);

    const isValidSearch = useCallback(() => {
        let hasValidSearchForm;

        if (hotelStandalone) {
            hasValidSearchForm = isHotelStandaloneValidSearch;
        } else if (isHotel || hotelPrime) {
            hasValidSearchForm = isHotelValidSearch;
        } else {
            hasValidSearchForm = isFlightValidSearch;
        }

        return hasValidSearchForm;
    }, [
        isHotelValidSearch,
        hotelPrime,
        isHotel,
        isHotelStandaloneValidSearch,
        isFlightValidSearch,
        hotelStandalone,
    ]);

    useEffect(() => {
        if (tryToLaunchSearch) {
            if (isValidSearch()) {
                trackSearchEvents();
                if (activeProduct === EntryPoint.HOTELS) {
                    setLoading(true);
                    doHotelSearch();
                } else if (dynPack) {
                    setLoading(true);
                    doDynpackSearch();
                } else {
                    doSearch();
                }
            } else {
                setLoading(false);
                trackErrorEvents();
                showSearchErrors();
            }
            setTryToLaunchSearch(false);
        }
    }, [
        activeProduct,
        doHotelSearch,
        doSearch,
        showSearchErrors,
        trackErrorEvents,
        trackSearchEvents,
        tryToLaunchSearch,
        dynPack,
        doDynpackSearch,
        isValidSearch,
    ]);

    useEffect(() => {
        if (!isVisible && loading) {
            setLoading(false);
        }
    }, [isVisible, loading]);

    const searchExtensionShouldBeTriggered = () => {
        const searchExtensionCount =
            parseInt(LocalStorageUtils.getValue('searchExtensionCount')) || 0;

        const searchExtensionReadableCount = searchExtensionCount + 1;
        const isCounterEven = searchExtensionReadableCount % 2 === 0;

        const searchExtensionShouldBeTriggered = !isCounterEven;

        LocalStorageUtils.setValue('searchExtensionCount', searchExtensionReadableCount);

        return searchExtensionShouldBeTriggered;
    };

    const getIsEnabledSearchExtension = () => {
        const isHotelsEnabled = Config.get('products')?.includes('hotels');
        const isExtensionScope = !isInspirationalSearch;
        return isHotelsEnabled && isExtensionScope && flightSearchExtensionEnabled;
    };

    const getIsEnabledSearchExtensionHome = () => {
        const { isHomeHotelSearchExtensionFenced } = ABUtils.getABs();
        const isHotelsEnabled = Config.get('products')?.includes('hotels');
        const isExtensionScope = !isInspirationalSearch;
        const isNotHotelsEntryPoint = Config.get('entryPoint') !== EntryPoint.HOTELS;
        return (
            isHotelsEnabled &&
            isExtensionScope &&
            isNotHotelsEntryPoint &&
            (flightSearchExtensionToHomePagePublicEnabled ||
                (flightSearchExtensionToHomePageFencedEnabled && isHomeHotelSearchExtensionFenced))
        );
    };

    const trackSearchExtensionEvents = ({ hotelHome = false }) => {
        if (hotelHome) {
            trackEvent({
                action: TrackingUtils.ACTION.SEARCH_EXTENSION,
                label: TrackingUtils.LABEL.searchExtension(),
            });
            TrackingUtils.trackComponentDisplayed({ id: `home_${SEARCH_EXTENSION_HOTEL_HOME}` });
        }
    };

    const saveSearchExtensionData = () => {
        const defaultSearchDestination = currentSearch?.segments[0]?.destination;
        const defaultSearchDate = DateUtils.toString(currentSearch?.segments[0]?.date);

        const adjustedCurrentSearch = {
            tripType: currentSearch.tripType,
            segments: currentSearch.segments.map((segment) => ({
                ...segment,
                ...(segment.destination ? {} : { destination: defaultSearchDestination }),
                ...(segment.date
                    ? { date: DateUtils.toString(segment.date) }
                    : { date: defaultSearchDate }),
            })),
        };
        if (defaultSearchDestination && defaultSearchDate) {
            SessionStorageUtils.setJSON(
                SEARCH_EXTENSION_NON_PRIME_STORAGE_KEY,
                adjustedCurrentSearch,
            );
        }
    };

    const doSearchExtension = ({ hotelStandalone = false, hotelHome = false }) => {
        if (isValidSearch()) {
            trackSearchEvents();
            const flightSearchURL = SearchService.getSearchURL({
                ...currentSearch,
                dynPack,
                shouldShowResident,
                activeProduct,
                buyPath: buyPaths.FLIGHTS,
            });
            const newWindow = window.open(flightSearchURL, '_blank');
            if (newWindow) {
                if (hotelStandalone) {
                    LocalStorageUtils.setValue('searchExtensionCount', 1);
                }
                if (hotelHome) {
                    saveSearchExtensionData();
                }
                const searchExtensionUrl = SearchService.getSearchExtensionURL({
                    currentSearch: { ...currentSearch, hotelStandalone, hotelHome },
                });

                if (searchExtensionUrl) {
                    trackSearchExtensionEvents({ hotelHome });
                    window.history.pushState({}, '');
                    window.location.replace(searchExtensionUrl);
                }
            } else {
                trackEvent({
                    action: 'search-extension',
                    label: 'search-extension-blocked_pag:home',
                });
                window.location.href = flightSearchURL;
            }
        } else {
            trackErrorEvents();
            showSearchErrors();
        }
    };

    const onClick = () => {
        // The next if statement is for a prime user
        if (getIsEnabledSearchExtension() && searchExtensionShouldBeTriggered()) {
            doSearchExtension({ hotelStandalone: true });
            // The next if statement is for a non-prime user
        } else if (getIsEnabledSearchExtensionHome()) {
            doSearchExtension({ hotelHome: true });
        } else if (activeProduct === EntryPoint.HOTELS) {
            setLoading(true);
            setHotel(dynPack || hotelPrime || hotelStandalone);
            if (isValidSearch()) {
                trackSearchEvents();
                doHotelSearch();
            } else {
                setLoading(false);
                trackErrorEvents();
                showSearchErrors();
            }
        } else {
            setHotel(dynPack || hotelPrime || hotelStandalone);
            if (!tryToLaunchSearch) {
                setTryToLaunchSearch(true);
            }
        }
    };

    return render({ loading, onClick });
};

SearchButton.propTypes = {
    activeProduct: EntryPointProp.isRequired,
    buyPaths: BuyPathContextProp.isRequired,
    currentSearch: SearchProp.isRequired,
    dynPack: PropTypes.bool.isRequired,
    isInspirationalSearch: PropTypes.bool.isRequired,
    isFlightValidSearch: PropTypes.bool.isRequired,
    isHotelValidSearch: PropTypes.bool.isRequired,
    isHotelStandaloneValidSearch: PropTypes.bool.isRequired,
    render: PropTypes.func.isRequired,
    setHotel: PropTypes.func.isRequired,
    shouldShowResident: PropTypes.bool.isRequired,
    showErrors: PropTypes.func.isRequired,
    trackEvent: PropTypes.func.isRequired,
    validationErrors: PropTypes.arrayOf(ProductManagerErrorProp).isRequired,
    extraTracking: PropTypes.func,
    onErrorCallback: PropTypes.func,
    getAsyncSearch: PropTypes.func,
};

SearchButton.defaultProps = {
    dynPack: false,
    extraTracking: () => {},
    onErrorCallback: () => {},
};

const mapStateToProps = (state, props) => ({
    activeProduct: state.activeProduct.activeProduct,
    currentSearch: state.currentSearch,
    isInspirationalSearch: isInspirationalSearch(state),
    isFlightValidSearch: isFlightValidSearch(state, props),
    isHotelValidSearch: isHotelValidSearch(state, props),
    isHotelStandaloneValidSearch: isHotelStandaloneValidSearch(state, props),
    shouldShowResident: getResidentDiscountProps(state).show,
    validationErrors: getValidationErrorsSelector(state, props),
    isHotel: isHotelProductActive(state),
    nonPrimeRoom: getNonPrimeHotelRoom(state),
    getAsyncSearch,
});

export const ConnectedSearchButton = connect(mapStateToProps, {
    showErrors,
    showNonPrimeHotelErrors,
    setHotel,
})(withBuyPathContext(withPageContext(SearchButton)));
